import React, {useState, useEffect, useRef} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMembership } from '../../hooks/MembershipProvider/MembershipProvider';
import './PhoneVerification.css';
import {useForm} from "react-hook-form";
import {formatTime} from "../../utils/UtilsFunc";
import Modals from "../Modals/Modals";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
type SetStateAction<T> = T | ((prevState: T) => T);
interface PhoneVerificationProps {}

const PhoneVerification: React.FC<PhoneVerificationProps> = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useMembership();
  const {register,watch,formState:{errors},} = useForm({mode:"onChange"});
  const [kuid, setKuid] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'input' | 'verify'>('input');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(300);
  const [userState, setUserState] = useState<SetStateAction<any>>({});
  const phoneNumber = watch("phoneNum");
  const phoneCode = watch("code");
  let startTimer:any
  useEffect(() => {
    // URL 파라미터에서 전화번호 인증아이디 (kuid) 가져오기
    const kuidFromUrl = searchParams.get('kuid');
    if (kuidFromUrl) {
      setKuid(kuidFromUrl);
    }
  }, [searchParams]);

  // SMS 인증 코드 발송
  const sendVerificationCode = async () => {
    if (!phoneNumber) {
      setError('전화번호를 입력해주세요.');
      return;
    }
    console.log(phoneNumber.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, '$1-$2-$3'))
    startTimer =  setInterval(()=>{
      setTimer((prev)=>prev - 1);
    },1000);
    
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/sms/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone_number: phoneNumber.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, '$1-$2-$3'),
          kuid: kuid
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSessionId(data.session_id);
        setTimer(300);
        setStep('verify');
        setMessage('인증 코드가 발송되었습니다. 5분 내에 입력해주세요.');
      } else {
        setError(data.message || '인증 코드 발송에 실패했습니다.');
      }
    } catch (error) {
      setError('서버 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  // SMS 인증 코드 확인
  const verifyCode = async () => {
    if (!phoneCode) {
      setError('인증 코드를 입력해주세요.');
      return;
    }
    setIsLoading(true);
    setError('');
    

    try {
      const response = await fetch(`${API_BASE_URL}/sms/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          verification_code: phoneCode.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, '$1-$2-$3'),
          session_id: sessionId
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        // 전화번호 인증 완료 처리
        // await completePhoneVerification();
        const user = data?.user;
        login(user, user.access_token);
        setUserState({
          show:true,
          userInfo:user,
        });
        clearInterval(startTimer);
      } else {
        setError(data.message || '인증 코드가 올바르지 않습니다.');
        setUserState({
          show:true,
          error:true,
        })
      }
    } catch (error) {
      setError('서버 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // 인증 코드 재발송
  const resendCode = async () => {
    setIsLoading(true);
    setError('');
    setTimer(300);
    try {
      const response = await fetch(`${API_BASE_URL}/sms/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          session_id: sessionId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage('인증 코드가 재발송되었습니다.');
      } else {
        setError(data.message || '인증 코드 재발송에 실패했습니다.');
      }
    } catch (error) {
      setError('서버 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    return ()=> {
      clearInterval(startTimer)
    }
  }, []);
  useEffect(() => {
    if(0 >= timer) {  // 제한시간 초과 될경우
      alert(`제한시간이 초과 되었습니다.`);
    }
  }, [timer]);
  
  return (
    <div className={`content_wrap phone_verification`}>
      
      <div className={`section_wrap`}>
        <div className={`sec sec_1`}>
          <h3 className={`h3`}>
            소중한 기록을 안전하게 보호하기 위해<br/>
            휴대폰 번호 인증이 필요해요.
          </h3>
          <div className={`form_group`}>
            <label className={`label body1_sb`}>휴대폰 번호</label>
            <div className={`flex`}>
              <input
                {...register('phoneNum', {
                  pattern: {
                    value: /^\d+$/, // ^ $로 전체 매칭
                    message: "이메일 형식이 아닙니다.",
                  },
                })}
                inputMode="numeric"
                onKeyDown={(e) => {
                  if (!/[0-9]/.test(e.key) && e.key !== "Backspace") {
                    e.preventDefault();
                  }
                }}
                maxLength={11}
                placeholder={`휴대폰 번호 (전화번호만 입력)`}
                className={`form_control`}
                type={`text`}
              />
              {(step === `verify`) && (
                <button
                  onClick={resendCode}
                  className={`btn btn-outline-primary`}
                >
                  재전송
                </button>
              )}
              {(step === `input`) && (
                <button
                  onClick={sendVerificationCode}
                  disabled={!(phoneNumber?.length >= 11)}
                  className={`btn btn-primary ${(phoneNumber?.length >= 11) ? '' : 'dis'}`}
                >
                  인증번호 전송
                </button>
              )}
            </div>
          </div>
          {/*인증번호 발송시 메세지*/}
          {/*{(message !== ``) && (*/}
          {/*)}*/}
          {(step === `verify`) && (
            <div className={`alert_txt`}>
              <p className={`txt body2`}>{message}</p>
            </div>
          )}
        </div>
        {/**----------------------------------------인증번호 전송시 화면 노출----------------------------------------**/}
        {(step === `verify`) && (
          <>
            <div className={`sec sec_2`}>
              <div className={`form_group`}>
                <label className={`label body1_sb`}>인증번호</label>
                <div className={`flex relative form_control_input_box`}>
                  <input
                    {...register('code', {
                      pattern: {
                        value: /^\d+$/, // ^ $로 전체 매칭
                        message: "이메일 형식이 아닙니다.",
                      },
                    })}
                    inputMode="numeric"
                    onKeyDown={(e) => {
                      if (!/[0-9]/.test(e.key) && e.key !== "Backspace") {
                        e.preventDefault();
                      }
                    }}
                    maxLength={6}
                    placeholder={`6자리 숫자`}
                    className={`form_control code`}
                    type={`text`}
                  />
                  <div className={`timer`}>{formatTime(timer)}</div>
                </div>
                <div className={`alert_txt`}>
                  <p className={`txt body2`}>{error}</p>
                </div>
              </div>
            </div>
            <div className={`fixed bottom-0 left-0 fixed_btn_box`}>
              <div className={`sec sec_3`}>
                <button
                  className={`btn btn-primary ${(phoneCode?.length >= 6) ? '' : 'dis'}`}
                  onClick={verifyCode}
                  disabled={!(phoneCode?.length >= 6)}
                >
                  인증 완료
                </button>
              </div>
            </div>
          </>
        )}
      
      </div>
      <Modals
        {...userState}
        imgSrc={`./contents/join_end.png`}
        navigate={navigate}     // 내비
        // userName={userName}     // 유저이름
        message={error}
        alertType={`${(userState?.error) ? `same_user`:`phone_verification`}`}
      />
    </div>
  );
};

export default PhoneVerification; 